import { assign, createMachine, spawn } from 'xstate';
import fieldMachine from './fieldMachine';
export function getValues(actors) {
    if (!actors)
        return [];
    return Object.entries(actors).reduce((acc, [fieldName, fieldActorRef]) => {
        return Object.assign(Object.assign({}, acc), { [fieldName]: fieldActorRef.getSnapshot().context.value });
    }, {});
}
function hasBeenValidated(fieldActorRef) {
    const snapshot = fieldActorRef.getSnapshot();
    return (snapshot.matches('validation.valid') ||
        snapshot.matches('validation.invalid'));
}
async function validateFields(actors) {
    /*
     * We have to include fields that have been already validated b/c fields that are in the `valid`
     * state may have been validated against stale values (ex: if it was considered valid in the past
     * due to a previously entered value of a different field)
     */
    const fieldActorRefs = Object.values(actors);
    // start validating fields
    fieldActorRefs.forEach((fieldActorRef) => fieldActorRef.send('VALIDATE'));
    // Field validations are asynchronous.
    await Promise.all(fieldActorRefs.map((fieldActorRef) => new Promise((resolve) => {
        if (hasBeenValidated(fieldActorRef)) {
            resolve();
            return;
        }
        /*
         * If it is validating, subscribe to the state change,
         * unsubscribe and then resolve the promise
         */
        const subscription = fieldActorRef.subscribe(() => {
            if (hasBeenValidated(fieldActorRef)) {
                subscription.unsubscribe();
                resolve();
            }
        });
    })));
    // Compile error object
    return Object.entries(actors).reduce((errors, [fieldName, fieldActorRef]) => {
        const snapshot = fieldActorRef.getSnapshot();
        return Object.assign(Object.assign({}, errors), { [fieldName]: snapshot.context.error });
    }, {});
}
export default createMachine({
    predictableActionArguments: true,
    id: 'formMachine',
    type: 'parallel',
    schema: {
        events: {}
    },
    context: {
        actors: {},
        fieldConfigs: [],
        fieldErrors: {}
    },
    states: {
        lifecycle: {
            initial: 'initializing',
            states: {
                initializing: {
                    always: {
                        target: '#editing',
                        actions: assign({
                            actors: (context) => {
                                const { fieldConfigs } = context;
                                const initialValues = fieldConfigs.reduce((acc, [name, initialValue]) => {
                                    return Object.assign(Object.assign({}, acc), { [name]: initialValue });
                                }, {});
                                return fieldConfigs.reduce((actors, [name, initialValue, mutable, validators]) => {
                                    if (actors[name]) {
                                        return Object.assign({}, actors);
                                    }
                                    return Object.assign(Object.assign({}, actors), { [name]: spawn(fieldMachine.withContext({
                                            initialValue,
                                            mutable,
                                            name,
                                            validators,
                                            value: initialValue,
                                            values: initialValues
                                        }), { name }) });
                                }, {});
                            }
                        })
                    }
                },
                editing: {
                    id: 'editing',
                    on: {
                        SUBMIT: {
                            target: '#validating'
                        }
                    }
                },
                validating: {
                    id: 'validating',
                    entry: ({ actors }) => {
                        Object.values(actors).forEach((fieldActorRef) => fieldActorRef.send('FREEZE'));
                    },
                    exit: ({ actors }) => {
                        Object.values(actors).forEach((fieldActorRef) => fieldActorRef.send('UNFREEZE'));
                    },
                    invoke: {
                        id: 'validate',
                        src: async (context) => {
                            const fieldErrors = await validateFields(context.actors);
                            if (Object.values(fieldErrors).filter(Boolean).length) {
                                // eslint-disable-next-line @typescript-eslint/no-throw-literal
                                throw fieldErrors;
                            }
                        },
                        onDone: {
                            target: ['#valid', '#submitting']
                        },
                        onError: {
                            target: ['#invalid', '#editing'],
                            actions: assign({
                                fieldErrors: (_, { data }) => data
                            })
                        }
                    }
                },
                submitting: {
                    id: 'submitting',
                    entry: ({ actors }) => {
                        Object.values(actors).forEach((fieldActorRef) => fieldActorRef.send('FREEZE'));
                    },
                    exit: ({ actors }) => {
                        Object.values(actors).forEach((fieldActorRef) => fieldActorRef.send('UNFREEZE'));
                    },
                    invoke: {
                        id: 'submit',
                        src: async ({ actors, onSubmit, onSubmitError, submit }) => {
                            const values = getValues(actors);
                            if (submit) {
                                try {
                                    await submit(values);
                                }
                                catch (err) {
                                    if (onSubmitError) {
                                        onSubmitError(err);
                                    }
                                    throw err;
                                }
                            }
                            setTimeout(() => {
                                if (onSubmit) {
                                    onSubmit(values);
                                }
                            });
                        },
                        onDone: {
                            target: ['#success', '#editing'],
                            actions: ({ actors }) => {
                                // Commit the changes to each field
                                Object.values(actors).forEach((fieldActorRef) => fieldActorRef.send('COMMIT'));
                            }
                        },
                        onError: {
                            target: ['#error', '#editing']
                        }
                    }
                }
            }
        },
        validation: {
            initial: 'unvalidated',
            states: {
                unvalidated: {
                    id: 'unvalidated'
                },
                valid: {
                    id: 'valid'
                },
                invalid: {
                    id: 'invalid',
                    entry: ({ fieldErrors, onInvalid }) => {
                        if (onInvalid) {
                            onInvalid(fieldErrors);
                        }
                    },
                    on: {
                        SUBMIT: '#unvalidated'
                    }
                }
            }
        },
        submit: {
            initial: 'pristene',
            states: {
                pristene: {
                    id: 'pristene'
                },
                success: {
                    id: 'success'
                },
                error: {
                    id: 'error'
                }
            }
        }
    },
    on: {
        CHANGE: {
            target: ['#editing', '#unvalidated'],
            actions: assign({
                fieldConfigs: ({ actors, fieldConfigs = [] }, { name, value }) => {
                    const fieldMeta = fieldConfigs.find(([fieldName]) => fieldName === name);
                    const index = fieldConfigs.indexOf(fieldMeta);
                    if (fieldMeta) {
                        fieldMeta.splice(1, 1, value);
                    }
                    fieldConfigs.splice(index, 1, fieldMeta);
                    const values = getValues(actors);
                    Object.values(actors).forEach((fieldActorRef) => {
                        fieldActorRef.send({
                            type: 'SIBLING_CHANGE',
                            values
                        });
                    });
                    return [...fieldConfigs];
                }
            })
        },
        SET_HANDLERS: {
            actions: assign({
                onInvalid: (_, { onInvalid }) => onInvalid,
                onSubmit: (_, { onSubmit }) => onSubmit,
                onSubmitError: (_, { onSubmitError }) => onSubmitError,
                submit: (_, { submit }) => submit
            })
        }
    }
});
